import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useOverwatchAccess from '../../hooks/useOverwatchAccess';
import { API_URL } from '../../utils/api';
import './Overwatch.css';

/**
 * Superuser-only admin page for managing overwatch access. Lets the owner
 * grant or revoke access without touching Django admin. Users without the
 * ``can_manage`` flag are redirected away.
 */
export default function OverwatchAdmin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasAccess, canManage, loading, refresh } = useOverwatchAccess();
  const { getAuthHeaders } = useAuth();
  const [accessList, setAccessList] = useState([]);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [notes, setNotes] = useState('');
  const [searching, setSearching] = useState(false);

  const loadList = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/overwatch/access`, { headers: getAuthHeaders() });
    if (res.ok) setAccessList(await res.json());
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!loading && (!hasAccess || !canManage)) {
      navigate('/', { replace: true });
      return;
    }
    if (canManage) loadList();
  }, [loading, hasAccess, canManage, navigate, loadList]);

  const doSearch = async () => {
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/overwatch/user-search`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
      if (res.ok) setSearchResults(await res.json());
    } finally {
      setSearching(false);
    }
  };

  const grant = async (userId) => {
    await fetch(`${API_URL}/api/overwatch/access`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, notes }),
    });
    setSearchResults([]);
    setQuery('');
    setNotes('');
    await loadList();
    await refresh();
  };

  const revoke = async (accessId) => {
    if (!window.confirm(t('overwatch.confirmRevoke'))) return;
    await fetch(`${API_URL}/api/overwatch/access/${accessId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await loadList();
  };

  if (loading || !canManage) return null;

  return (
    <div className="overwatch-page">
      <button
        onClick={() => navigate('/overwatch')}
        style={{
          background: 'transparent',
          color: '#888',
          border: 'none',
          cursor: 'pointer',
          marginBottom: 12,
          fontSize: '0.9rem',
        }}
      >
        ← {t('overwatch.backToDashboard')}
      </button>

      <h1>🛡️ {t('overwatch.adminTitle')}</h1>
      <p className="overwatch-subtitle">
        {t('overwatch.adminSubtitle')}
      </p>

      <div className="overwatch-admin-grid">
        <div className="overwatch-admin-section">
          <h2>{t('overwatch.grantAccess')}</h2>
          <div className="overwatch-filters">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('overwatch.searchUserPlaceholder')}
              style={{ flex: 1 }}
              onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
            />
            <button
              onClick={doSearch}
              disabled={searching}
              style={{
                background: '#2b2d31',
                color: '#e5e5e5',
                border: '1px solid #333',
                borderRadius: 6,
                padding: '8px 16px',
                cursor: 'pointer',
              }}
            >
              {searching ? '...' : t('nav.search')}
            </button>
          </div>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('overwatch.notePlaceholder')}
            style={{
              width: '100%',
              background: '#16181b',
              color: '#e5e5e5',
              border: '1px solid #333',
              borderRadius: 6,
              padding: '8px 12px',
              marginBottom: 10,
            }}
          />
          {searchResults.length > 0 && (
            <table className="overwatch-table">
              <thead>
                <tr>
                  <th>{t('overwatch.colUsername')}</th>
                  <th>Discord</th>
                  <th>Steam</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((u) => (
                  <tr key={u.user_id}>
                    <td>{u.username}</td>
                    <td>{u.discord_username || '—'}</td>
                    <td>{u.steam_username || '—'}</td>
                    <td>
                      {u.has_access ? (
                        <span className="review-status cleared">{t('overwatch.statusActive')}</span>
                      ) : (
                        <button
                          onClick={() => grant(u.user_id)}
                          style={{
                            background: '#1f3a24',
                            color: '#6bd480',
                            border: 'none',
                            borderRadius: 4,
                            padding: '4px 10px',
                            cursor: 'pointer',
                          }}
                        >
                          + {t('overwatch.grantAccess')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="overwatch-admin-section">
          <h2>{t('overwatch.currentOverwatchers', { count: accessList.length })}</h2>
          {accessList.length === 0 ? (
            <div className="overwatch-empty">{t('overwatch.noneAssignedYet')}</div>
          ) : (
            <table className="overwatch-table">
              <thead>
                <tr>
                  <th>{t('overwatch.colUser')}</th>
                  <th>{t('overwatch.colStatus')}</th>
                  <th>{t('overwatch.colDate')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {accessList.map((a) => (
                  <tr key={a.id}>
                    <td>
                      {a.username}
                      {a.discord_username && (
                        <div style={{ fontSize: '0.75rem', color: '#888' }}>
                          Discord: {a.discord_username}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`review-status ${a.is_active ? 'cleared' : 'flagged'}`}>
                        {a.is_active ? t('overwatch.statusActive') : t('overwatch.statusRevoked')}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {a.granted_at ? new Date(a.granted_at).toLocaleDateString('sv-SE') : '—'}
                    </td>
                    <td>
                      {a.is_active && (
                        <button
                          onClick={() => revoke(a.id)}
                          style={{
                            background: '#3a1f25',
                            color: '#ff5a5a',
                            border: 'none',
                            borderRadius: 4,
                            padding: '4px 10px',
                            cursor: 'pointer',
                          }}
                        >
                          {t('overwatch.remove')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
